import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./container.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import * as kagekiri from 'kagekiri';
import equalityFn from 'zustand/shallow';
import { FeatureFlags } from '~/modules/feature_flags';
import { selectSharedLabels, WireI18n } from '~/modules/stores/i18nStore';
import { routerStore, selectIsStandalone } from '~/modules/stores/routerStore';
class Container extends LightningElement {
  constructor(...args) {
    super(...args);
    this.labels = void 0;
    // bound to x-lazy child template via lwc:dynamic attribute
    this.pageComponent = null;
    // Standalone pages (e.g. select-org) render without the site header/footer.
    this.isStandalone = false;
    this.isMobileNavigationOpen = false;
    this.isFilterDrawerVisible = false;
  }
  routerStoreCallback({
    pageComponent,
    isStandalone
  }) {
    if (this.pageComponent !== pageComponent) {
      this.isFilterDrawerVisible = false;
    }
    this.pageComponent = pageComponent;
    this.isStandalone = isStandalone;
    if (isStandalone) {
      this.isMobileNavigationOpen = false;
      this.isFilterDrawerVisible = false;
    }
  }
  handleMobileNavigationChange(event) {
    this.isMobileNavigationOpen = Boolean(event.detail?.open);
    this.syncSiteChromeInert();
  }
  handleFilterDrawerVisibilityChange(event) {
    this.isFilterDrawerVisible = Boolean(event.detail?.visible);
    this.syncSiteChromeInert();
  }
  get mobileNavigationInert() {
    return this.isMobileNavigationOpen ? '' : undefined;
  }
  renderedCallback() {
    this.syncSiteChromeInert();
  }
  syncSiteChromeInert() {
    const template = this.template;
    if (!template) {
      return;
    }
    const overlayOpen = this.isMobileNavigationOpen || this.isFilterDrawerVisible;
    template.querySelector('ace-header-wrapper')?.toggleAttribute('inert', this.isFilterDrawerVisible);
    template.querySelector('ace-footer')?.toggleAttribute('inert', overlayOpen);
    // LWC 7 routes `inert` on custom elements to a property, so the former
    // declarative binding was a no-op. toggleAttribute writes the HTML attribute.
    template.querySelector('ace-agentforce-chat')?.toggleAttribute('inert', overlayOpen);
  }
  get showHeaderFooter() {
    return !this.isStandalone;
  }
  get rootContainerCss() {
    const classList = ['root-container'];
    if (this.isStandalone) {
      classList.push('root-container-standalone');
    }
    if (this.pageComponent?.pageContainerBottomPadding === 'none') {
      classList.push('root-container-no-bottom-padding');
    }
    return classList.join(' ');
  }
  get logoRef() {
    return kagekiri.querySelector('ace-header .header-logo');
  }

  /**
   * This function manages the visibility of the footer to prevent Cumulative Layout Shift (CLS).
   * The footer is hidden until all page components are fully loaded.
   * Once the components are loaded, the footer is displayed.
   *
   * @returns {string} - Returns an empty string to show the footer when page components are loaded.
   *                     Returns 'hide-footer' to hide the footer while components are loading.
   */
  get footerClass() {
    if (this.isStandalone) {
      return 'hide-footer';
    }
    if (this.pageComponent) {
      return '';
    }
    return 'hide-footer';
  }
  get showAgentforceChat() {
    return FeatureFlags.FF_AGENTFORCE_CHAT_BUTTON && !this.isStandalone;
  }

  /**
   * Move focus to the main content region, bypassing the header/navigation (WCAG 2.4.1).
   * The href fragment is kept for no-JS and copy-link semantics, but focus is moved
   * programmatically because native fragment navigation is unreliable across shadow DOM.
   */
  handleSkipToMain(event) {
    event.preventDefault();
    const mainContent = this.refs?.mainContent;
    if (mainContent instanceof HTMLElement) {
      mainContent.focus();
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(Container, {
  wire: {
    routerStoreCallback: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: state => ({
            pageComponent: selectPageComponent(state),
            isStandalone: selectIsStandalone(state)
          }),
          equalityFn
        };
      }
    },
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: selectSharedLabels
        };
      }
    }
  },
  fields: ["pageComponent", "isStandalone", "isMobileNavigationOpen", "isFilterDrawerVisible"]
});
const __lwc_component_class_internal = _registerComponent(Container, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;
function selectPageComponent(state) {
  return state.pageComponent;
}